import React, { createContext, FC, ReactNode, useMemo } from 'react';

import { Organization, OrganizationAccountType, useMeQuery, User } from '../types/api.graphql';
import { alphabetizeComparer } from '../utils/sort';
import { useAuthContext } from './AuthContext';

type Nullable<T> = T | null;

interface SessionContextProps {
  me: Nullable<User>;
  hasDealWithPaidSubscription: boolean;
  hasComplianceDealership: boolean;
  activeDealerships: ReadonlyArray<Organization>;
}

interface SessionProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextProps>({
  me: null,
  hasDealWithPaidSubscription: false,
  hasComplianceDealership: false,
  activeDealerships: [],
});

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const { authToken } = useAuthContext();
  const { data } = useMeQuery({ skip: !authToken });

  const hasDealWithPaidSubscription = data?.me?.organizations?.some((org) => org?.paidSubscription);
  const activeDealerships =
    data?.me?.organizations
      ?.filter(({ cif }) => !!cif)
      ?.sort(alphabetizeComparer((item) => item.name, 'ascending', { ignorePunctuation: true })) || [];
  const hasComplianceDealership = useMemo(
    () => activeDealerships.some(({ accountType }) => accountType === OrganizationAccountType.Compliance),
    [activeDealerships],
  );

  return (
    <SessionContext.Provider
      value={{
        me: data?.me || null,
        hasDealWithPaidSubscription,
        hasComplianceDealership,
        activeDealerships,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext: () => SessionContextProps = () => {
  return React.useContext(SessionContext);
};
